import { cn } from "@babybjorn/ui";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, useState, type FC } from "react";
import AddToCartButton from "../../components/AddToCartButton";

type ProductStickyNavLink = {
	label: string;
	link: string;
};

type ProductStickyNavProps = React.HTMLAttributes<HTMLElement> & {
	price: string;
	links: ProductStickyNavLink[];
	addToCart?: () => Promise<void>;
};

export const ProductStickyNav: FC<ProductStickyNavProps> = ({
	price,
	links,
	addToCart,
	...props
}) => {
	const { t } = useTranslation();
	const [activeLink, setActiveLink] = useState<string>("#product");

	useEffect(() => {
		const visibleSections = new Set<string>();
		const observerOptions = {
			root: null,
			rootMargin: "-50% 0px -50% 0px",
			threshold: 0,
		};

		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				const targetId = entry.target.id;
				const sectionLink = `#${targetId}`;

				if (entry.isIntersecting) {
					visibleSections.add(sectionLink);
				} else {
					visibleSections.delete(sectionLink);
				}
			}

			if (visibleSections.size > 0) {
				const firstVisibleLink = links.find((link) =>
					visibleSections.has(link.link),
				);
				if (firstVisibleLink) {
					setActiveLink(firstVisibleLink.link);
				}
			}
		}, observerOptions);

		for (const linkItem of links) {
			const elementId = linkItem.link.replace("#", "");
			// eslint-disable-next-line unicorn/prefer-query-selector
			const element = document.getElementById(elementId);

			if (element) {
				observer.observe(element);
			}
		}

		return () => {
			observer.disconnect();
		};
	}, [links]);

	return (
		<nav
			className={cn(
				"sticky bottom-0 z-10 bg-white py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.025),0_-2px_4px_-2px_rgba(0,0,0,0.025)] transition-transform md:py-4",
				{
					"translate-y-full": activeLink === "#product",
				},
			)}
			aria-hidden={activeLink === "#product"}
			{...(activeLink === "#product" && { inert: "" })}
			{...props}
		>
			<div className="gap-bb mr-[max(var(--spacing-bb),96px)] ml-[var(--spacing-bb)] flex max-w-full items-center justify-between">
				<ul className={cn("gap-bb bb-text-label text-gray-2 hidden md:flex")}>
					{links.map(({ label, link }) => (
						<li key={link}>
							<button
								onClick={() => {
									const element = document.querySelector(link);
									if (!element) return;
									element.scrollIntoView({
										behavior: "smooth",
									});
								}}
								className={cn({
									"text-gray-4": activeLink === link,
								})}
							>
								{label}
							</button>
						</li>
					))}
				</ul>

				<div className="gap-bb flex items-center max-md:w-full">
					<div className="bb-text-body">{price}</div>

					{typeof addToCart === "function" && (
						<AddToCartButton
							label={t("Add to Cart")}
							onClick={addToCart}
							alwaysBig
							divClassContainer="max-md:!w-full"
						/>
					)}
				</div>
			</div>
		</nav>
	);
};
